import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getResponse } from 'services/api';
import { EventCandidate } from '../types';

// Server-side event search hook. Reuses the existing GET /events endpoint
// with date=all so past events show up too. Only fires when the user has
// typed something — the suggestions list (from the event-suggestions
// endpoint) covers the empty-input case.
interface ApiEvent {
	id?: number;
	ID?: number;
	name: string;
	time_zone?: string;
	start_date?: string;
	event_days?: Array<{ start_date: string }>;
}

const evID = (e: ApiEvent): number | undefined => e.id ?? e.ID;

const useEventSearch = (query: string, enabled: boolean) =>
	useQuery({
		queryKey: ['ticket-importer-event-search', query],
		enabled,
		// 30s stale so successive typed letters reuse results when scrubbing back.
		staleTime: 30_000,
		queryFn: async () => {
			const { data } = await getResponse(`events?per_page=50&date=all&search=${encodeURIComponent(query)}`);
			return data as ApiEvent[];
		},
	});

// Format an ISO timestamp in the event's IANA timezone (e.g.
// "America/Los_Angeles") if provided, falling back to the browser's locale.
// We do this so a Punchup ops person reviewing imports always sees the
// venue-local show time — which is what they'll cross-reference against
// the CSV — instead of the time converted to wherever they happen to be.
const formatStartDate = (iso: string, timeZone?: string): string => {
	if (!iso) return '';
	const d = new Date(iso);
	if (isNaN(d.getTime())) return iso;
	const opts: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	};
	if (timeZone) opts.timeZone = timeZone;
	try {
		return new Intl.DateTimeFormat(undefined, opts).format(d);
	} catch {
		// Bad timezone string — fall back to browser local.
		return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
	}
};

interface Props {
	suggestions: EventCandidate[];
	selectedId: number | null;
	onSelect: (eventId: number | null, eventName?: string, startDate?: string) => void;
}

// Combobox that shows the auto-detected suggestions when idle and lets the
// user type to search the partner's full event catalogue.
//
// Label resolution for the closed state goes through three fallbacks:
//   1. Local cache populated when the user selected from search results
//      (most authoritative — survives the live-search query being cleared).
//   2. The suggestion list passed in by the parent (auto-detected matches).
//   3. The live search results, in case the parent re-rendered before we
//      could record the choice locally.
//
// Without (1), picking an event from a search result and closing the dropdown
// would clear `query`, disabling useEventSearch and dropping the cache, so
// the closed button would fall back to "— pick an event —" even though
// `selectedId` is set.
export const EventCombobox = ({ suggestions, selectedId, onSelect }: Props): JSX.Element => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [localLabels, setLocalLabels] = useState<Record<number, { label: string; subtitle: string }>>({});
	const wrapperRef = useRef<HTMLDivElement>(null);

	// Resolve the currently-selected event from whatever's around.
	const search = useEventSearch(query, open && query.length >= 1);

	const cached = selectedId !== null ? localLabels[selectedId] : undefined;
	const selectedFromSuggestions = suggestions.find((c) => c.event_id === selectedId);
	const selectedFromSearch = search.data?.find((e) => evID(e) === selectedId);

	let closedLabel = '';
	if (cached) {
		closedLabel = cached.subtitle ? `${cached.label} — ${cached.subtitle}` : cached.label;
	} else if (selectedFromSuggestions) {
		closedLabel = `${selectedFromSuggestions.event_name}${
			selectedFromSuggestions.start_date
				? ` — ${formatStartDate(selectedFromSuggestions.start_date, selectedFromSuggestions.time_zone)}`
				: ''
		}`;
	} else if (selectedFromSearch) {
		const start = selectedFromSearch.start_date || selectedFromSearch.event_days?.[0]?.start_date || '';
		closedLabel = `${selectedFromSearch.name}${
			start ? ` — ${formatStartDate(start, selectedFromSearch.time_zone)}` : ''
		}`;
	}

	// Close on outside click.
	useEffect(() => {
		if (!open) return;
		const handler = (e: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setOpen(false);
				setQuery('');
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [open]);

	const optionsToShow = useMemo(() => {
		if (query.trim().length === 0) {
			return suggestions.map((c) => ({
				id: c.event_id,
				label: c.event_name,
				subtitle: c.start_date
					? `${formatStartDate(c.start_date, c.time_zone)}${
							c.hours_off > 0 ? ` · ${Math.round(c.hours_off)}h off` : ''
					  }`
					: '',
				exact: c.name_exact_match,
			}));
		}
		const results = search.data ?? [];
		return results
			.map((e) => {
				const id = evID(e);
				if (typeof id !== 'number') return null;
				const start = e.start_date || e.event_days?.[0]?.start_date || '';
				return {
					id,
					label: e.name,
					subtitle: start ? formatStartDate(start, e.time_zone) : '',
					exact: false,
				};
			})
			.filter((x): x is NonNullable<typeof x> => x !== null);
	}, [query, suggestions, search.data]);

	return (
		<div ref={wrapperRef} style={{ position: 'relative', minWidth: 340 }}>
			{!open && (
				<button
					type="button"
					onClick={() => setOpen(true)}
					style={{
						width: '100%',
						padding: '8px 10px',
						border: '1px solid #d1d5db',
						borderRadius: 6,
						background: '#fff',
						textAlign: 'left',
						fontSize: 14,
						color: closedLabel ? '#111827' : '#6b7280',
						cursor: 'pointer',
					}}
				>
					{closedLabel || '— pick an event —'}
				</button>
			)}
			{open && (
				<>
					<input
						autoFocus
						type="text"
						placeholder="Search by name…"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						style={{
							width: '100%',
							padding: '8px 10px',
							border: '1px solid #6366f1',
							borderRadius: 6,
							fontSize: 14,
						}}
					/>
					<div
						style={{
							position: 'absolute',
							top: '100%',
							left: 0,
							right: 0,
							marginTop: 4,
							background: '#fff',
							border: '1px solid #e5e7eb',
							borderRadius: 6,
							boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
							maxHeight: 320,
							overflowY: 'auto',
							zIndex: 10,
						}}
					>
						{search.isFetching && query && (
							<div style={{ padding: 10, fontSize: 13, color: '#6b7280' }}>Searching…</div>
						)}
						{!search.isFetching && optionsToShow.length === 0 && (
							<div style={{ padding: 10, fontSize: 13, color: '#9ca3af' }}>
								{query ? 'No matches.' : 'No suggestions.'}
							</div>
						)}
						{optionsToShow.map((opt) => (
							<button
								key={opt.id}
								type="button"
								onClick={() => {
									setLocalLabels((prev) => ({
										...prev,
										[opt.id]: { label: opt.label, subtitle: opt.subtitle },
									}));
									onSelect(opt.id, opt.label, opt.subtitle);
									setOpen(false);
									setQuery('');
								}}
								style={{
									display: 'block',
									width: '100%',
									textAlign: 'left',
									padding: '8px 10px',
									border: 'none',
									background: opt.id === selectedId ? '#eef2ff' : 'transparent',
									cursor: 'pointer',
									fontSize: 14,
								}}
								onMouseEnter={(e) => {
									if (opt.id !== selectedId) e.currentTarget.style.background = '#f9fafb';
								}}
								onMouseLeave={(e) => {
									if (opt.id !== selectedId) e.currentTarget.style.background = 'transparent';
								}}
							>
								<div>
									<strong>{opt.label}</strong>
									{opt.exact ? <span style={{ color: '#10b981', marginLeft: 6 }}>✓</span> : null}
								</div>
								{opt.subtitle && <small style={{ color: '#6b7280' }}>{opt.subtitle}</small>}
							</button>
						))}
					</div>
				</>
			)}
		</div>
	);
};
